import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from './Header';
import Footer from './Footer';
import { useJobs, useCategories, useLocations } from '../hooks/useApi';
import company1 from '../assets/img/company_logo_1.png';
import { Link } from 'react-router-dom';

const BrowseJobs = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  
  // Use React Query hooks
  const { data: jobsData, isLoading: jobsLoading, error: jobsError } = useJobs();
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  const { data: locationsData, isLoading: locationsLoading } = useLocations();
  
  // Combine loading states
  const isLoading = jobsLoading || categoriesLoading || locationsLoading;
  
  // Filter state
  const [searchKeywords, setSearchKeywords] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedSalaryRange, setSelectedSalaryRange] = useState('');
  const [selectedJobType, setSelectedJobType] = useState('');
  const [selectedDesignation, setSelectedDesignation] = useState('');
  const [selectedExperience, setSelectedExperience] = useState('');
  const [selectedQualification, setSelectedQualification] = useState('');
  const [sortBy, setSortBy] = useState('Most Recent');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Define salary ranges
  const salaryRanges = [
    { min: 0, max: 3, label: 'Under 3 LPA' },
    { min: 3, max: 5, label: '3 LPA - 5 LPA' },
    { min: 5, max: 7, label: '5 LPA - 7 LPA' },
    { min: 7, max: 10, label: '7 LPA - 10 LPA' },
    { min: 10, max: 14, label: '10 LPA - 14 LPA' },
    { min: 14, max: 18, label: '14 LPA - 18 LPA' },
    { min: 18, max: 24, label: '18 LPA - 24 LPA' },
    { min: 24, max: Infinity, label: 'Above 24 LPA' }
  ];

  // Helper function to match salary range
  const matchSalaryRange = (salary, range) => {
    if (!range) return true;
    const salaryNum = parseFloat(salary) || 0;
    const rangeObj = salaryRanges.find(r => r.label === range);
    if (!rangeObj) return true;
    
    if (rangeObj.label.includes('Under')) {
      return salaryNum < rangeObj.max;
    } else if (rangeObj.label.includes('Above')) {
      return salaryNum >= rangeObj.min;
    } else {
      return salaryNum >= rangeObj.min && salaryNum < rangeObj.max;
    }
  };

  // Update the getMainCities function to handle the location data structure
  const getMainCities = (locations) => {
    const mainCities = new Set();
    Object.values(locations).forEach(location => {
      // Split by comma and get the last part (main city)
      const parts = location.split(',');
      const mainCity = parts[parts.length - 1].trim();
      mainCities.add(mainCity);
    });
    return Array.from(mainCities).sort();
  };

  // Update the locations processing
  const locations = useMemo(() => {
    if (!locationsData) return [];
    
    const mainCities = getMainCities(locationsData);
    
    return mainCities.map(city => ({
      id: city.toLowerCase().replace(/\s+/g, '-'),
      name: city,
      count: jobsData?.filter(job => {
        const jobLocation = job.location_name || job.location || '';
        return typeof jobLocation === 'string' && 
               jobLocation.toLowerCase().includes(city.toLowerCase());
      }).length || 0
    })).filter(location => location.count > 0); // Only show cities with jobs
  }, [locationsData, jobsData]);

  const jobTypes = useMemo(() => {
    if (!categoriesData || !jobsData) return [];
    const categoryCounts = {};
    
    // Count jobs in each category
    jobsData.forEach(job => {
      const categoryId = job.category;
      if (categoryId && categoriesData[categoryId]) {
        const categoryName = categoriesData[categoryId];
        categoryCounts[categoryName] = (categoryCounts[categoryName] || 0) + 1;
      }
    });

    // Convert to array format
    return Object.entries(categoryCounts).map(([label, count]) => ({
      id: label.toLowerCase().replace(/\s+/g, '-'),
      label,
      count
    }));
  }, [categoriesData, jobsData]);

  const salaryRangeCounts = useMemo(() => {
    if (!jobsData) return {};
    const counts = {};
    jobsData.forEach(job => {
      const salary = parseFloat(job.package) || 0;
      const range = salaryRanges.find(r => {
        if (r.label.includes('Under')) {
          return salary < r.max;
        } else if (r.label.includes('Above')) {
          return salary >= r.min;
        } else {
          return salary >= r.min && salary < r.max;
        }
      });
      if (range) {
        counts[range.label] = (counts[range.label] || 0) + 1;
      }
    });
    return counts;
  }, [jobsData]);

  // Process dynamic designations
  const designations = useMemo(() => {
    if (!jobsData) return [];
    const designationCounts = {};
    jobsData.forEach(job => {
      // Get designation from job data, default to 'Not Specified' if missing
      const designation = job.designation || 'Not Specified';
      // Count occurrences of each designation
      designationCounts[designation] = (designationCounts[designation] || 0) + 1;
    });
    // Convert to array format with counts
    return Object.entries(designationCounts)
      .map(([label, count]) => ({
        id: label.toLowerCase().replace(/\s+/g, '-'),
        label,
        count
      }))
      .sort((a, b) => b.count - a.count); // Sort by count descending
  }, [jobsData]);

  // Process dynamic experiences
  const experiences = useMemo(() => {
    if (!jobsData) return [];
    const experienceCounts = {};
    jobsData.forEach(job => {
      // Get experience from job data, default to 'Not Specified' if missing
      const experience = job.experience || 'Not Specified';
      // Count occurrences of each experience level
      experienceCounts[experience] = (experienceCounts[experience] || 0) + 1;
    });
    // Convert to array format with counts
    return Object.entries(experienceCounts)
      .map(([label, count]) => ({
        id: label.toLowerCase().replace(/\s+/g, '-'),
        label,
        count
      }))
      .sort((a, b) => b.count - a.count); // Sort by count descending
  }, [jobsData]);

  // Process dynamic qualifications
  const qualifications = useMemo(() => {
    if (!jobsData) return [];
    const qualificationCounts = {};
    jobsData.forEach(job => {
      // Get qualification from job data, default to 'Not Specified' if missing
      const qualification = job.qualification || 'Not Specified';
      // Count occurrences of each qualification
      qualificationCounts[qualification] = (qualificationCounts[qualification] || 0) + 1;
    });
    // Convert to array format with counts
    return Object.entries(qualificationCounts)
      .map(([label, count]) => ({
        id: label.toLowerCase().replace(/\s+/g, '-'),
        label,
        count
      }))
      .sort((a, b) => b.count - a.count); // Sort by count descending
  }, [jobsData]);

  // Process job data
  const processedJobs = useMemo(() => {
    if (!jobsData) return [];
    return jobsData.map(job => ({
      id: job.job_id,
      jobTitle: job.title || '',
      jobDescription: job.description || '',
      jobPosition: job.position || '',
      skills: [],
      jobType: job.position || 'Full Time',
      package: job.package ? (job.package).toString() : '0',
      location: job.location || 'Not Specified',
      category: job.category ? job.category.toString() : '',
      companyDetails: job.company ? job.company.toString() : '',
      image: '',
      job_code: job.job_code || '',
      designation: job.designation || '',
      experience: job.experience || '',
      qualification: job.qualification || '',
      location_name: job.location_name || '',
      company_name: job.company_name || '',
      company_image_url: job.company_image_url || ''
    }));
  }, [jobsData]);

  // Apply category filter if needed
  const filteredJobs = useMemo(() => {
    if (!processedJobs) return [];
    if (!category || !categoriesData) return processedJobs;
    
    // Find category ID from the slug
    const categoryEntry = Object.entries(categoriesData).find(
      ([id, name]) => name.toLowerCase().replace(/\s+/g, '-') === category.toLowerCase()
    );
    
    if (!categoryEntry) return processedJobs;
    
    const categoryId = categoryEntry[0];
    return processedJobs.filter(job => job.category === categoryId);
  }, [processedJobs, category, categoriesData]);

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchKeywords);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Filter jobs based on all criteria
  const filteredJobsByCriteria = useMemo(() => {
    if (!filteredJobs) return [];
    return filteredJobs.filter(job => {
      const matchesKeyword = !searchQuery || 
        (job.jobTitle?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (job.jobDescription?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (job.skills || []).some(skill => (skill?.toLowerCase() || '').includes(searchQuery.toLowerCase()));
      
      const jobLocation = job.location_name || job.location || '';
      const matchesLocation = !selectedLocation || 
        (typeof jobLocation === 'string' && jobLocation.toLowerCase().includes(selectedLocation.toLowerCase()));
      
      const matchesSalary = !selectedSalaryRange || 
        matchSalaryRange(job.package, selectedSalaryRange);
      
      // Update job type matching to use categories
      const matchesJobType = !selectedJobType || 
        (job.category && categoriesData && categoriesData[job.category] === selectedJobType);

      const matchesDesignation = !selectedDesignation ||
        (typeof job.designation === 'string' && job.designation.toLowerCase() === selectedDesignation.toLowerCase());

      const matchesExperience = !selectedExperience ||
        (typeof job.experience === 'string' && job.experience.toLowerCase() === selectedExperience.toLowerCase());

      const matchesQualification = !selectedQualification ||
        (typeof job.qualification === 'string' && job.qualification.toLowerCase() === selectedQualification.toLowerCase());

      return matchesKeyword && matchesLocation && matchesSalary && 
             matchesJobType && matchesDesignation && matchesExperience && 
             matchesQualification;
    });
  }, [filteredJobs, searchQuery, selectedLocation, selectedSalaryRange, selectedJobType, selectedDesignation, selectedExperience, selectedQualification, categoriesData]);

  // Sort jobs based on selected criteria
  const sortedJobs = useMemo(() => {
    if (!filteredJobsByCriteria) return [];
    
    return [...filteredJobsByCriteria].sort((a, b) => {
      switch (sortBy) {
        case 'Most Recent':
          // Sort by job_id as a fallback if no date is available
          return b.id - a.id;
        case 'Most Viewed':
          // Sort by job code or title length as a proxy for popularity
          return (b.job_code || '').length - (a.job_code || '').length;
        case 'Most Search':
          // Sort by job title length as a proxy for search relevance
          return (b.jobTitle || '').length - (a.jobTitle || '').length;
        default:
          return 0;
      }
    });
  }, [filteredJobsByCriteria, sortBy]);

  // Calculate pagination
  const indexOfLastJob = currentPage * itemsPerPage;
  const indexOfFirstJob = indexOfLastJob - itemsPerPage;
  const currentJobs = sortedJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobsByCriteria.length / itemsPerPage);

  const handleJobClick = (jobId, jobTitle, jobCode) => {
    // If job code is available, use the short URL format
    if (jobCode) {
      navigate(`/jobs/${jobCode}`);
      return;
    }
    
    // If job title is available, create a URL-friendly slug
    if (jobTitle) {
      const slugifiedTitle = jobTitle
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
      
      if (slugifiedTitle) {
        navigate(`/jobs/${slugifiedTitle}`);
        return;
      }
    }
    
    // Fallback to the original URL format
    const formattedTitle = jobTitle ? jobTitle.toLowerCase().replace(/\s+/g, '+') : 'job';
    navigate(`/job-detail/${formattedTitle}/${jobId}`);
  };

  // Get the display name of the category from slug
  const getCategoryDisplayName = (slug) => {
    if (!slug) return 'All Jobs';
    
    if (categoriesData) {
      const categoryEntry = Object.entries(categoriesData).find(
        ([id, name]) => name.toLowerCase().replace(/\s+/g, '-') === slug.toLowerCase()
      );
      
      return categoryEntry ? categoryEntry[1] : slug;
    }
    
    return slug;
  };

  // Generate meta description
  const generateMetaDescription = () => {
    const categoryName = getCategoryDisplayName(category);
    const jobCount = filteredJobsByCriteria.length;
    
    if (category) {
      return `Browse ${jobCount} ${categoryName} jobs. Find the best ${categoryName} opportunities with salary information, company details, and job requirements.`;
    }
    
    return `Browse ${jobCount} job opportunities across various categories. Find the best jobs with salary information, company details, and job requirements.`;
  };

  // Generate canonical URL
  const generateCanonicalUrl = () => {
    const baseUrl = window.location.origin;
    return category 
      ? `${baseUrl}/browse-jobs/${category}` 
      : `${baseUrl}/browse-jobs`;
  };

  return (
    <>
      <Helmet>
        <title>{category ? `${getCategoryDisplayName(category)} Jobs | Fresher Job Adda` : 'Browse Jobs | Fresher Job Adda'}</title>
        <meta name="description" content={generateMetaDescription()} />
        <link rel="canonical" href={generateCanonicalUrl()} />
        <meta property="og:title" content={category ? `${getCategoryDisplayName(category)} Jobs | Fresher Job Adda` : 'Browse Jobs | Fresher Job Adda'} />
        <meta property="og:description" content={generateMetaDescription()} />
        <meta property="og:url" content={generateCanonicalUrl()} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={category ? `${getCategoryDisplayName(category)} Jobs | Fresher Job Adda` : 'Browse Jobs | Fresher Job Adda'} />
        <meta name="twitter:description" content={generateMetaDescription()} />
      </Helmet>
      
      <Header />
      <div className="page-title">
        <div className="container">
          <div className="page-caption">
            <h2>
              {category 
                ? `${getCategoryDisplayName(category)} Jobs (${filteredJobsByCriteria.length})`
                : 'Browse Jobs'}
            </h2>
            <p>
                <Link to="/">Home</Link> 
              <i className="fa fa-angle-double-right"></i> 
              {category ? (
                <>
                  <Link to="/browse-jobs">Browse Jobs</Link>
                  <i className="fa fa-angle-double-right"></i>
                  {getCategoryDisplayName(category)}
                </>
              ) : (
                'Browse Jobs'
              )}
            </p>
          </div>
        </div>
      </div>

      <section className="padd-top-80 padd-bot-80">
        <div className="container">
          <div className="row">
            {/* Sidebar Start */}
            <div className="col-md-3 col-sm-5">
              <div className="widget-boxed padd-bot-0">
                <div className="widget-boxed-body">
                  <div className="search_widget_job">
                    <form onSubmit={handleSearch}>
                      <div className="field_w_search">
                        <input 
                          type="text" 
                          className="form-control" 
                          placeholder="Search jobs"
                          value={searchKeywords}
                          onChange={(e) => setSearchKeywords(e.target.value)}
                        />
                      </div>
                    
                      <button type="submit" className="btn btn-primary btn-block mb-3">
                        <i className="fa fa-search"></i> Search Jobs
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              {/* Offered Salary */}
              <div className="widget-boxed padd-bot-0">
                <div className="widget-boxed-header">
                  <h4>Offered Salary</h4>
                </div>
                <div className="widget-boxed-body">
                  <div className="side-list no-border">
                    <ul>
                      {salaryRanges.map((range) => (
                        <li key={range.label}>
                          <span className="custom-checkbox">
                            <input
                              type="checkbox"
                              id={range.label.toLowerCase().replace(/\s+/g, '-')}
                              checked={selectedSalaryRange === range.label}
                              onChange={() => setSelectedSalaryRange(range.label)}
                            />
                            <label htmlFor={range.label.toLowerCase().replace(/\s+/g, '-')}></label>
                          </span>
                          {range.label}
                          {/* <span className="pull-right">{salaryRangeCounts[range.label] || 0}</span> */}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Job Type */}
              <div className="widget-boxed padd-bot-0">
                <div className="widget-boxed-header">
                  <h4>Job Type</h4>

                </div>
                <div className="widget-boxed-body">
                  <div className="side-list no-border">
                    <ul>
                      {jobTypes.map((type) => (
                        <li key={type.id}>
                          <span className="custom-radio">
                            <input
                              type="radio"
                              id={type.id}
                              name="jobType"
                              checked={selectedJobType === type.label}
                              onChange={() => setSelectedJobType(type.label)}
                            />
                            <label htmlFor={type.id}></label>
                          </span>
                          {type.label}  ({type.count})               

                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Location */}
             <div className="widget-boxed padd-bot-0">
  <div className="widget-boxed-header">
    <h4>Location</h4>
  </div>
  <div className="widget-boxed-body">
    <div className="side-list no-border">
      <ul>
        <li>
          <span className="custom-radio">
            <input
              type="radio"
              id="location-all"
              name="location"
              checked={selectedLocation === ''}
              onChange={() => setSelectedLocation('')}
            />
            <label htmlFor="location-all"></label>
          </span>
          All Locations
        </li>
        {locations.map(location => (
          <li key={location.id}>
            <span className="custom-radio">
              <input
                type="radio"
                id={`location-${location.id}`}
                name="location"
                checked={selectedLocation === location.name}
                onChange={() => setSelectedLocation(location.name)}
              />
              <label htmlFor={`location-${location.id}`}></label>
            </span>
            {location.name} ({location.count})
          </li>
        ))}
      </ul>
    </div>
  </div>
</div>


            </div>
            {/* Sidebar End */}

            {/* Job Listings */}
            <div className="col-md-9 col-sm-7">
              <div className="row mrg-bot-20">
                <div className="col-md-4 col-sm-12 col-xs-12 browse_job_tlt">
                  <h4 className="job_vacancie">
                    {filteredJobsByCriteria.length} {category ? `${decodeURIComponent(category)} ` : ''}
                    Jobs & Vacancies
                  </h4>
                </div>
                <div className="col-md-8 col-sm-12 col-xs-12">
                  <div className="fl-right short_by_filter_list">
                    <div className="search-wide short_by_til">
                      <h5>Short By</h5>
                    </div>
                    <div className="search-wide full">
                      <div className="input-group">
                        <select 
                          className="wide form-control"
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          style={{ paddingLeft: '30px' }}
                        >
                          <option value="Most Recent">
                            <i className="fas fa-clock text-success"></i> Most Recent
                          </option>
                          <option value="Most Viewed">
                            <i className="fas fa-eye"></i> Most Viewed
                          </option>
                          <option value="Most Search">
                            <i className="fas fa-search  "></i> Most Search
                          </option>
                        </select>
                        <span className="input-group-addon" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}>
                          {sortBy === 'Most Recent' && <i className="fas fa-clock "></i>}
                          {sortBy === 'Most Viewed' && <i className="fas fa-eye "></i>}
                          {sortBy === 'Most Search' && <i className="fas fa-search"></i>}
                        </span>
                      </div>
                    </div>
                    <div className="search-wide full">
                      <div className="input-group">
                        <select 
                          className="wide form-control"
                          value={itemsPerPage}
                          onChange={(e) => {
                            setItemsPerPage(parseInt(e.target.value));
                            setCurrentPage(1);
                          }}
                          style={{ paddingLeft: '30px' }}
                        >
                          <option value="10">10 Per Page</option>
                          <option value="20">20 Per Page</option>
                          <option value="30">30 Per Page</option>
                          <option value="50">50 Per Page</option>
                        </select>
                        <span className="input-group-addon" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}>
                          <i className="fas fa-list "></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Job Listings */}
              {isLoading ? (
                <div className="col-12 text-center">
                  <div className="utf_empty_box">
                    <i className="fa fa-spinner fa-spin fa-3x mb-3" style={{ color: '#26ae61' }}></i>
                    <h3>Loading Jobs...</h3>
                    <p>Please wait while we fetch the latest job listings for you.</p>
                  </div>
                </div>
              ) : jobsError ? (
                <div className="col-12 text-center">
                  <div className="utf_empty_box">
                    <i className="fa fa-exclamation-triangle fa-3x mb-3" style={{ color: '#dc3545' }}></i>
                    <h3>Error Loading Jobs</h3>
                    <p>{jobsError.message || 'There was an error loading the job listings. Please try again later.'}</p>
                    <button 
                      className="btn btn-primary mt-3"
                      onClick={() => window.location.reload()}
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              ) : filteredJobsByCriteria.length === 0 ? (
                <div className="col-12 text-center mt-3">
  <div className="utf_empty_box">
    <i className="fa fa-search fa-3x mb-3" style={{ color: '#26ae61' }}></i>
    <h3>No Jobs Found</h3>
    <p>
      {category 
        ? `No jobs available in ${decodeURIComponent(category)} category` 
        : 'No jobs found matching your criteria. Try adjusting your filters.'}
    </p>
    
    <div className="d-flex justify-content-center">
      <button 
        className="btn btn-success mt-3"
        onClick={() => {
          setSearchKeywords('');
          setSelectedLocation('');
          setSelectedSalaryRange('');
          setSelectedJobType('');
          setSelectedDesignation('');
          setSelectedExperience('');
          setSelectedQualification('');
        }}
      >
        Clear Filters
      </button>
    </div>

  </div>
</div>

              ) : (
                <div className="row">
                  {currentJobs.map((job) => (
                    <div className="col-md-3 col-sm-6" key={job.id}>
                      <div
                        className="utf_grid_job_widget_area pt-0"
                        onClick={() => handleJobClick(job.id, job.jobTitle, job.job_code)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="u-content px-0" style={{ position: "relative" }}>
                          <div className="avatar-square box-80" style={{ width: "100%", position: "relative" }}>
                            <a onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleJobClick(job.id, job.jobTitle, job.job_code);
                            }}>
                              <div 
                                className="category-icon-fallback"
                                style={{
                                  display: 'flex',
                                  width: "100%",
                                  height: "200px",
                                  backgroundColor: "#f8f9fa",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  fontSize: "4rem",
                                  color: "#26ae61",
                                  borderRadius: "8px",
                                  padding: "2rem",
                                  position: "relative",
                                  zIndex: 1
                                }}
                              >
                                {/* Fallback icon - always present but hidden by default */}
                                <div 
                                  className="fallback-icon"
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "#f8f9fa",
                                    zIndex: 1
                                  }}
                                >
                                  <i
                                    className="fa-solid fa-house"
                                    style={{
                                      fontSize: "3.5rem",
                                      color: "#ccc"
                                    }}
                                  ></i>
                                </div>

                                {/* Company image */}
                                {job.company_image_url?.trim() ? (
                                  <img
                                    src={job.company_image_url}
                                    alt={job.company_name}
                                    loading="lazy"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      const fallbackIcon = e.target.parentNode.querySelector(".fallback-icon");
                                      if (fallbackIcon) {
                                        fallbackIcon.style.display = "flex";
                                      }
                                    }}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      zIndex: 2
                                    }}
                                    className="company-logo"
                                  />
                                ) : null}
                              </div>
                              
                           
                            </a>
                          </div>
                          <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 3 }}>
                            <span className="job-type bg-success border-1 text-white position-absolute top-2 start-2">
                              {categoriesData && job.category ? categoriesData[job.category] : job.category}
                            </span>
                          </div>
                          <h5 className="pt-3">
                            <a onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleJobClick(job.id, job.jobTitle, job.job_code);
                            }}>
                              {job.jobTitle}
                            </a>
                          </h5>
                          <p className="text-muted mb-0">
                            <i className="fa fa-briefcase"></i> {job.jobPosition}
                          </p>
                          <p className="text-muted mb-0">
                            <i className="fa fa-industry text-capitalize"></i> {job.company_name}
                          </p>
                          <p className="text-muted py-0 my-0">
                            <i className="fas fa-wallet"></i> {job.package}+ LPA
                          </p>
                          <p className="text-muted py-0 my-0 text-capitalize">
                            <i className="fas fa-map-marker-alt"></i> {job.location_name}
                          </p>
                          <p className="text-muted py-0 my-0 text-capitalize">
  <i className="fas fa-code
"></i> {job.job_code}
</p>
                        </div>
                        <div className="utf_apply_job_btn_item">
                          <a
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleJobClick(job.id, job.jobTitle, job.job_code);
                            }}
                            className="btn job-browse-btn btn-radius br-light"
                          >
                            <i className="fa fa-paper-plane"></i> Apply Now
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {filteredJobsByCriteria.length > itemsPerPage && (
                <div className="utf_flexbox_area padd-0">
                  <ul className="pagination">
                    <li className="page-item">
                      <a
                        className="page-link"
                        href="#"
                        aria-label="Previous"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        <span aria-hidden="true">«</span>
                        <span className="sr-only">Previous</span>
                      </a>
                    </li>
                    {[...Array(totalPages)].map((_, i) => (
                      <li 
                        key={i} 
                        className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}
                      >
                        <a
                          className="page-link"
                          href="#"
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </a>
                      </li>
                    ))}
                    <li className="page-item">
                      <a
                        className="page-link"
                        href="#"
                        aria-label="Next"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        <span aria-hidden="true">»</span>
                        <span className="sr-only">Next</span>
                      </a>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default BrowseJobs; 